import { useCallback, useContext, useEffect, useState, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation, Trans } from 'react-i18next'
import cn from 'clsx'
import _keys from 'lodash/keys'
import _isUndefined from 'lodash/isUndefined'

import NavBar from '../../components/NavBar/NavBar'
import TopBar from './components/TopBar/TopBar'
import SideBar from './components/Sidebar/Sidebar'
import Banner from '../../components/Banner/Banner'
import NetEmissionsChart from '../../components/NetEmissionsChart/NetEmissionsChart'
import NetEmissionsChartNew, {
  ChartType
} from '../../components/NetEmissionsChartNew/NetEmissionsChartNew'
import BaselineChart from '../../components/BaselineChart/BaselineChart'
import BaselineChartNew from '../../components/BaselineChartNew/BaselineChartNew'
import Map from '../../components/Map/Map'
import DataAttribution from '../../components/DataAttribution/DataAttribution'
import { useShowBanner } from '../../hooks/useHasGlobalAverageData'
import useFeatureNewCharts from '../../hooks/useFeatureNewCharts'
import { BarChartGranularity } from '../../components/BarChartNew/BarChartNew'
import AuthContext from 'auth_context'
import UIContext from 'ui_context'
import * as U from 'utils'
import * as H from 'hooks'
import * as C from 'components'

import {
  type EmissionsFilters,
  type EmissionsGroup,
  type EmissionsData,
  type Emissions
} from 'types/data'

import './style.scss'

const CLASS_NAME = 'cst-page-insights'
const CLASS_NAME_MAP = 'cst-page-insights-map'

export enum InsightsView {
  ANALYTICS = 'Analytics',
  MAP = 'Map'
}

type Props = {
  className?: string
  view: InsightsView
}

export default function Insights({
  className,
  view = InsightsView.ANALYTICS
}: Props) {
  const { user } = useContext(AuthContext)
  const location = useLocation()
  const { search } = location
  const pathName = location.pathname
  const urlEID = search.split('=')[1]
  const isNewChartsEnabled = useFeatureNewCharts()

  H.useEnforceAuthByNavigate(user)
  H.useDocumentTitle(view)

  const { t } = useTranslation()
  const { emissions } = useContext(UIContext)
  const showBanner = useShowBanner()
  const urlEmissions = emissions?.find(({ id }) => id === urlEID)

  const emissionsMinDate = H.useFunctionMemo<Date | null>(
    U.getEmissionsMinDate,
    emissions ?? []
  )
  const emissionsMaxDate = H.useFunctionMemo<Date | null>(
    U.getEmissionsMaxDate,
    emissions ?? []
  )
  const hasSingleYearOfData =
    emissionsMinDate?.getFullYear() === emissionsMaxDate?.getFullYear()

  const [filters, setFilters] = useState<EmissionsFilters>(
    U.getNewEmissionsFilters(emissions, emissionsMinDate, emissionsMaxDate)
  )

  const { start, end, sortMode, groupMode } = filters
  const setFilterValue = useCallback(
    (key: string, value: any) => {
      setFilters((prevFilters: EmissionsFilters) => ({
        ...prevFilters,
        [key]: value
      }))
    },
    [setFilters]
  )

  const [selectedEmissions, setSelectedEmissions] = useState<Emissions[]>([])
  const { infoModalEmissions, onInfoModalClose, onInfoModalOpen } =
    H.useInfoModal()

  const finalClassName = H.useClassName(CLASS_NAME, className)

  useEffect((): void => {
    if (emissions === null) {
      setFilterValue('start', null)
      setFilterValue('end', null)
    } else {
      setFilterValue('start', emissionsMinDate)
      setFilterValue('end', emissionsMaxDate)
    }
  }, [setFilterValue, emissions, emissionsMinDate, emissionsMaxDate])

  const filteredEmissions = H.useFunctionMemo<Emissions[]>(
    U.getFilteredEmissions,
    emissions ?? [],
    filters
  )

  const sortedEmissions = H.useFunctionMemo<Emissions[]>(
    U.getSortedEmissions,
    filteredEmissions,
    sortMode
  )

  const groupedEmissions = H.useFunctionMemo<EmissionsGroup[]>(
    U.getGroupedEmissions,
    sortedEmissions,
    groupMode,
    sortMode
  )

  const filteredSelectedEmissions = H.useFunctionMemo<Emissions[]>(
    U.getFilteredEmissions,
    selectedEmissions,
    filters
  )

  const selectedGroups: EmissionsGroup[] = H.useFunctionMemo(
    U.getSelectedEmissionGroups,
    groupedEmissions,
    filteredSelectedEmissions
  )

  const mainGraphEmissions = useMemo((): Emissions[] => {
    if (selectedGroups.length > 1) {
      return selectedGroups.map((group: EmissionsGroup) =>
        U.getMergedEmissionsForGroup(group)
      )
    } else if (filteredSelectedEmissions.length === 0) {
      return sortedEmissions
    } else {
      return filteredSelectedEmissions
    }
  }, [sortedEmissions, selectedGroups, filteredSelectedEmissions])

  const selectedPolygonUUIDs = useMemo(
    (): string[] =>
      selectedEmissions.map(({ feature }) => feature.properties.feature_uuid),
    [selectedEmissions]
  )

  useEffect((): void => {
    if (urlEmissions !== undefined) {
      setTimeout((): void => {
        setSelectedEmissions([urlEmissions])
      }, 500)
    }
  }, [urlEmissions])

  const mainGraphYearlyEmissions = useMemo(
    (): Emissions[] =>
      mainGraphEmissions.map(({ data, ...otherEmissionsData }): Emissions => {
        const yearlyData: EmissionsData = {}

        _keys(data).forEach((key: string): void => {
          const year = key.substring(0, 4)

          if (_isUndefined(yearlyData[year])) {
            yearlyData[year] = data[key]
          } else {
            yearlyData[year] += data[key]
          }
        })

        return {
          ...otherEmissionsData,
          data: yearlyData
        }
      }),
    [mainGraphEmissions]
  )

  const mainGraphIsSplit = useMemo(
    (): boolean => selectedGroups.length > 1,
    [selectedGroups]
  )

  const hasData = filteredEmissions.length > 0 || start === null || end === null

  const numberOfSelectedPolygons = filteredSelectedEmissions.length
  const userHasSelectedPolygons = numberOfSelectedPolygons > 0

  const annualNEEChartTitle = userHasSelectedPolygons
    ? t('net_emissions_chart.analytics_annual.title', {
        count: numberOfSelectedPolygons
      })
    : t('net_emissions_chart.analytics_annual.title_default')

  const monthlyNEEChartTitle = userHasSelectedPolygons
    ? t('net_emissions_chart.analytics_monthly.title', {
        count: numberOfSelectedPolygons
      })
    : t('net_emissions_chart.analytics_monthly.title_default')

  const ChartPlaceholder = () => (
    <div>
      <div className="cst-chart-header-title">
        <Trans i18nKey="net_emissions_chart.title_total_nee_per_polygon" />
      </div>
      <div className="cst-chart-placeholder">
        <Trans i18nKey="analytics.chart_placeholder_text" />
      </div>
      {!hasSingleYearOfData && (
        <>
          <div className="cst-chart-header-title">
            <Trans i18nKey="baseline_chart.title" />
          </div>
          <div className="cst-chart-placeholder">
            <Trans i18nKey="analytics.chart_placeholder_text" />
          </div>
        </>
      )}
    </div>
  )
  const topBarTitle =
    pathName === '/analytics' ? t('analytics.title') : t('map.title')

  return (
    <div className={cn(finalClassName, { hasBanner: showBanner })}>
      {showBanner && <Banner />}
      <NavBar />
      <SideBar
        filters={filters}
        emissions={sortedEmissions}
        selectedGroups={selectedGroups}
        setFilterValue={setFilterValue}
        groupedEmissions={groupedEmissions}
        onShowFeatureInfo={onInfoModalOpen}
        selectedEmissions={selectedEmissions}
        setSelectedEmissions={setSelectedEmissions}
      />
      <div
        className={`${view === InsightsView.MAP ? CLASS_NAME_MAP : CLASS_NAME}-content-wrapper-outer`}
      >
        <TopBar
          emissionsMinDate={emissionsMinDate}
          emissionsMaxDate={emissionsMaxDate}
          setFilterValue={setFilterValue}
          emissions={emissions}
          filters={filters}
          title={topBarTitle}
        />
        {!hasData && (
          <C.NoDataSkeleton label={t('analytics.no_data_message')} />
        )}
        {hasData && pathName === '/analytics' && (
          <C.FluidContentWrapper>
            <>
              {isNewChartsEnabled ? (
                <>
                  <NetEmissionsChartNew
                    title={annualNEEChartTitle}
                    emissions={mainGraphYearlyEmissions}
                    granularity={BarChartGranularity.ANNUAL}
                  />
                  <NetEmissionsChartNew
                    title={monthlyNEEChartTitle}
                    emissions={mainGraphEmissions}
                    granularity={BarChartGranularity.MONTHLY}
                  />
                </>
              ) : (
                <>
                  <NetEmissionsChart
                    split={mainGraphIsSplit}
                    title={annualNEEChartTitle}
                    emissions={mainGraphYearlyEmissions}
                    hideMonth
                  />
                  <NetEmissionsChart
                    split={mainGraphIsSplit}
                    title={monthlyNEEChartTitle}
                    emissions={mainGraphEmissions}
                  />
                </>
              )}
              {userHasSelectedPolygons ? (
                <>
                  {isNewChartsEnabled ? (
                    <NetEmissionsChartNew
                      emissions={filteredSelectedEmissions}
                      title={t(
                        'net_emissions_chart.title_total_nee_per_polygon'
                      )}
                      granularity={BarChartGranularity.MONTHLY}
                      variant={ChartType.LINE}
                    />
                  ) : (
                    <NetEmissionsChart
                      emissions={filteredSelectedEmissions}
                      title={t(
                        'net_emissions_chart.title_total_nee_per_polygon'
                      )}
                      split
                    />
                  )}
                  {!hasSingleYearOfData &&
                    (isNewChartsEnabled ? (
                      <BaselineChartNew emissions={filteredSelectedEmissions} />
                    ) : (
                      <BaselineChart emissions={filteredSelectedEmissions} />
                    ))}
                </>
              ) : (
                <ChartPlaceholder />
              )}
              <div className="cst-analytics-data-attribution-wrapper">
                <DataAttribution />
              </div>
            </>
          </C.FluidContentWrapper>
        )}
        {hasData && pathName === '/map' && (
          <Map
            filteredEmissions={filteredEmissions}
            selectedEmissions={selectedEmissions}
            selectedPolygonUUIDs={selectedPolygonUUIDs}
            setSelectedEmissions={setSelectedEmissions}
          />
        )}
        <C.EmissionsDetailsModal
          emissions={infoModalEmissions}
          onClose={onInfoModalClose}
        />
      </div>
    </div>
  )
}
